import React, { FC, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveStore } from 'features/stores/ducks/stores-slice';
import { Store } from './types';
import { storesListContainerStyle, storesListStyle } from './style';
import { StoreItem, ScrollCb } from './components';
import { getActiveStore } from './ducks/get-stores';
import { useChangeScrollBehaviour } from './hooks/use-change-scroll-behaviour';

type Props = {
  stores: Store[];
  storesRegionId: string;
};

export const StoresList: FC<Props> = ({ storesRegionId, stores }) => {
  const dispatch = useDispatch();
  const activeStore = useSelector(getActiveStore);
  const listContainerRef = useRef<null | HTMLDivElement>(null);
  const listRef = useRef<null | HTMLDivElement>(null);

  const setActive = useCallback(
    (store: Store | null) => {
      dispatch(setActiveStore(store));
    },
    [dispatch],
  );

  useEffect(() => {
    setActive(null);
    listContainerRef.current?.scrollTo?.({ left: 0, top: 0, behavior: 'smooth' });
  }, [listContainerRef, setActive, storesRegionId]);

  const { spacerHeight } = useChangeScrollBehaviour({
    listContainerRef,
    listRef,
    activeStore,
    storesRegionId,
  });

  const scrollCallback: ScrollCb = (itemRef) => {
    listContainerRef.current?.scrollTo?.({
      left: 0,
      top: Number(itemRef.current?.offsetTop),
    });
  };

  return (
    <div className={storesListContainerStyle} ref={listContainerRef}>
      <div className={storesListStyle} role="list" ref={listRef}>
        {stores.map((store) => (
          <StoreItem
            key={store.id}
            active={activeStore?.id === store.id}
            setActive={setActive}
            storesRegionId={storesRegionId}
            scrollCb={scrollCallback}
            {...store}
          />
        ))}
        {spacerHeight > 0 && (
          <div id="store-list-spacer" data-qa="store-list-spacer" style={{ height: spacerHeight }} />
        )}
      </div>
    </div>
  );
};
